import { useState } from 'react';

import { validators, VALIDATION_MESSAGES } from '@/shared/utils/validator';

import {
  ErrorKey,
  ErrorsType,
  ValidateSignUpParams,
  ValidationContext,
} from '../types/SignUpTypes';

const initialErrors: ErrorsType = {
  email: '',
  businessRegistrationNumber: '',
  companyName: '',
  representativeName: '',
  phoneNumber: '',
  companyAddress: '',
  businessCertificate: '',
  isKoreanCompany: '',
  personalInfoAgree: '',
  termsOfServiceAgree: '',
};

export const useValidateSignUp = () => {
  const [errors, setErrors] = useState<ErrorsType>(initialErrors);

  const getFieldError = (
    field: ErrorKey,
    value: any,
    context?: {
      isEmailVerified?: boolean;
      selectedFile?: File | null;
      businessCertificateUrl?: string;
    },
  ): string => {
    switch (field) {
      case 'email': {
        const emailValidation = validators.email(value);

        if (!emailValidation.valid) return emailValidation.error;
        if (context?.isEmailVerified === false) {
          return VALIDATION_MESSAGES.email.notVerified;
        }

        return '';
      }

      case 'businessRegistrationNumber': {
        const businessRegistrationValidation =
          validators.businessRegistrationNumber(value);

        return businessRegistrationValidation.error;
      }

      case 'companyName': {
        const companyNameValidation = validators.companyName(value);

        return companyNameValidation.error;
      }

      case 'representativeName': {
        const representativeNameValidation =
          validators.representativeName(value);

        return representativeNameValidation.error;
      }

      case 'phoneNumber': {
        const phoneNumberValidation = validators.phoneNumber(value);

        return phoneNumberValidation.error;
      }

      case 'companyAddress': {
        const companyAddressValidation = validators.address(value);

        return companyAddressValidation.error;
      }

      case 'businessCertificate': {
        const businessCertificateValidation = validators.businessCertificate(
          context?.selectedFile,
          context?.businessCertificateUrl,
        );

        return businessCertificateValidation.error;
      }

      case 'isKoreanCompany': {
        const isKoreanCompanyValidation = validators.isKoreanCompany(value);

        return isKoreanCompanyValidation.error;
      }

      case 'personalInfoAgree': {
        const personalInfoAgreeValidation = validators.personalInfoAgree(value);

        return personalInfoAgreeValidation.error;
      }

      case 'termsOfServiceAgree': {
        const termsOfServiceAgreeValidation =
          validators.termsOfServiceAgree(value);

        return termsOfServiceAgreeValidation.error;
      }

      default:
        return '';
    }
  };

  const validateField = (
    field: ErrorKey,
    value: any,
    context?: ValidationContext,
  ) => {
    const error = getFieldError(field, value, context);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const validateSignUp = (params: ValidateSignUpParams) => {
    const { signupForm, isEmailVerified, selectedFile } = params;

    const nextErrors: ErrorsType = {
      email: getFieldError('email', signupForm.email, { isEmailVerified }),
      businessRegistrationNumber: getFieldError(
        'businessRegistrationNumber',
        signupForm.businessRegistrationNumber,
      ),
      companyName: getFieldError('companyName', signupForm.companyName),
      representativeName: getFieldError(
        'representativeName',
        signupForm.representativeName,
      ),
      phoneNumber: getFieldError('phoneNumber', signupForm.phoneNumber),
      companyAddress: getFieldError(
        'companyAddress',
        signupForm.companyAddress,
      ),
      businessCertificate: getFieldError('businessCertificate', null, {
        selectedFile,
        businessCertificateUrl: signupForm.businessCertificateUrl,
      }),
      isKoreanCompany: getFieldError(
        'isKoreanCompany',
        signupForm.isKoreanCompany,
      ),
      personalInfoAgree: getFieldError(
        'personalInfoAgree',
        signupForm.personalInfoAgree,
      ),
      termsOfServiceAgree: getFieldError(
        'termsOfServiceAgree',
        signupForm.termsOfServiceAgree,
      ),
    };

    setErrors(nextErrors);
    return Object.values(nextErrors).every(
      (errorMessage) => errorMessage === '',
    );
  };

  return { errors, validateField, validateSignUp, setErrors };
};
